import React, { useState, useEffect, useRef } from 'react'

// Only need one of these
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/**
 * An input field that manages formatting the text on blur to introduce dollar signs and commas.
 * It then removes these values when the field gets focus so that the user can easily make updates.
 *
 * @param {String} name the name of the field
 * @param {String} value the raw value of the input field (may be an invalid number)
 * @param {Function} valueChanged a callback function called when the value changes
 * @param {Function} formattedValueChanged a callback function called when the formatted value
 *  changes, may be called with a string that is not formatted as a number
 * @param {Function} validChanged a callback function called when the raw value changes
 *  from being a valid number and back again
 * @param {String} id the id to assign input to associate with labels
 * @returns
 */
export default function CurrencyInput({
  name,
  value,
  valueChanged,
  formattedValueChanged,
  validChanged,
  id,
  printWhiteText,
}) {
  const [inputState, setInputState] = useState({
    hasFocus: false,
    rawValue: value,
    formattedValue: '',
    isValid: false,
  })
  const inputRef = useRef(null)

  useEffect(() => {
    const number = Number(value)
    const newIsValid = value && value.length > 0 && !Number.isNaN(number)
    const newFormattedValue = newIsValid ? currencyFormatter.format(number) : value
    setInputState(prev => ({
      ...prev,
      formattedValue: newFormattedValue,
      isValid: newIsValid,
    }))
  }, [value, formattedValueChanged, valueChanged])

  useEffect(() => {
    if (inputState.hasFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputState.hasFocus])

  const placeholderText = 'Enter a dollar amount'

  const handleInputChange = e => {
    const rawValue = e.target.value
    const number = Number(rawValue)
    const newIsValid = rawValue.length > 0 && !Number.isNaN(number)
    const newFormattedValue = newIsValid ? currencyFormatter.format(number) : rawValue
    setInputState(prev => ({
      ...prev,
      rawValue,
      formattedValue: newFormattedValue,
      isValid: newIsValid,
    }))
    if (valueChanged) {
      valueChanged(rawValue)
    }
    if (formattedValueChanged) {
      formattedValueChanged(newFormattedValue)
    }
    if (validChanged) {
      validChanged(newIsValid)
    }
  }

  const handleInputBlur = () => {
    setInputState(prev => ({
      ...prev,
      hasFocus: false,
    }))
  }
  const handleInputFocus = () => {
    setInputState(prev => ({
      ...prev,
      hasFocus: true,
    }))
  }

  // We maintain two input fields, one which is not formatted and is used when the user
  // is updating the raw value and a second which displays the formatted value.
  return (
    <React.Fragment>
      <input
        ref={inputRef}
        type='text'
        className={`Input u-pt-hidden 
          ${inputState.hasFocus ? '' : 'u-hidden'} 
          ${inputState.isValid || value === '' ? 'u-marginBottom5gu' : 'Input--error'}`}
        name={name}
        placeholder={placeholderText}
        value={inputState.rawValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        data-testid={name}
        id={id}
      />
      <input
        type='text'
        className={`Input faux-input u-pt-hidden 
          ${inputState.hasFocus ? 'u-hidden' : ''} 
          ${inputState.isValid || value === '' ? 'u-marginBottom5gu' : 'Input--error'}`}
        name={`${name}-blurred`}
        placeholder={placeholderText}
        value={inputState.formattedValue}
        onChange={() => {}}
        onFocus={handleInputFocus}
        data-testid={`${name}-blurred`}
      />
      {inputState.isValid || value === '' ? null : (
        <p className='Form-help u-pt-hidden u-textColorAlert'>
          Please enter a numeric value without commas.
        </p>
      )}
      <p
        className={`${
          printWhiteText ? 'u-textColorWhite' : ''
        } download-label label-value u-hidden u-pt-block u-textSizeMsr u-textBold`}>
        {inputState.formattedValue}
      </p>
    </React.Fragment>
  )
}
